/**
 * Перекличка героев на старте боя (ISSUE-122, ISSUE-145, MADR 0030, MADR 0037): план показа и его покадровая математика, без PixiJS.
 *
 * Показ живёт только на экране: движок, журнал волны и повторы о нём не знают. Время — тики волны, поэтому окно поверх боя
 * ставит показ на паузу вместе с боем. Смещения в клетках поля от ног героя: вверх — минус, как на экране.
 */
import type { HeroId } from "@newfungame/contracts";

/** `wave` — короткая плашка «Волна N» перед обычной волной, позы только у героев, получивших уровень. */
export type IntroKind = "session" | "hero" | "boss" | "wave";

export interface IntroBeat {
  heroId: HeroId;
  /** Начало позы, тиков от начала показа. */
  start: number;
  length: number;
  /** Подпись над героем, тиков от начала показа; у босса подписей нет. */
  plate: { from: number; to: number } | null;
}

export interface IntroPlan {
  kind: IntroKind;
  /** Тик волны, с которого идёт показ. */
  startTick: number;
  /** Длина показа, тиков. */
  length: number;
  beats: IntroBeat[];
}

export interface IntroPose {
  /** Кадр анимации `skill`, тиков от начала позы. */
  ticks: number;
  /** Высота прыжка, клеток. */
  hop: number;
  /** Яркость свечения вокруг героя. */
  glow: number;
}

/** Куда рисовать эффекты позы (поле). */
export interface IntroFxSink {
  sprite(
    kind: "muzzle" | "explosion" | "lightning" | "fire" | "ice",
    x: number,
    y: number,
    ticks: number,
    size: number,
  ): void;
  bomb(x: number, y: number): void;
  tracer(x: number, y: number, dx: number, dy: number): void;
  arc(x0: number, y0: number, x1: number, y1: number, alpha: number): void;
  glow(x: number, y: number, size: number, alpha: number): void;
  ring(x: number, y: number, radius: number, alpha: number): void;
}

const FIRST_TICK = 5;
const HOP_TICKS = 10;
const HOP_CELLS = 0.35;

/** Цвет героя в эффектах позы. */
export const HERO_TINT: Record<HeroId, number> = {
  gunner: 0xffd25a,
  bomber: 0xff9a3c,
  sparky: 0x8fe0ff,
  blaze: 0xff7a2c,
  frosty: 0xbfeaff,
  radiation: 0x9dff4a,
  cryo: 0x8fe6ff,
  laser: 0xff4a6a,
  plague: 0xb06aff,
};

const beat = (heroId: HeroId, start: number, length: number, plateTo: number | null): IntroBeat => ({
  heroId,
  start,
  length,
  plate: plateTo === null ? null : { from: start + 2, to: plateTo },
});

const planOf = (kind: IntroKind, startTick: number, length: number, beats: IntroBeat[]): IntroPlan => ({
  kind,
  startTick,
  length,
  beats,
});

/** Новый герой: он один, крупная подпись, 3 с. */
export function heroIntro(heroId: HeroId, startTick: number): IntroPlan {
  return planOf("hero", startTick, 90, [beat(heroId, FIRST_TICK, 75, 82)]);
}

/** Полная перекличка: все по очереди, шаг короче с ростом числа героев, чтобы уложиться в 3 с. */
function rollCall(kind: "session" | "boss", heroes: readonly HeroId[], startTick: number): IntroPlan {
  const n = heroes.length;
  if (n === 1) return planOf(kind, startTick, 58, [beat(heroes[0]!, FIRST_TICK, 45, 52)]);
  const step = Math.min(16, Math.round(54 / (n - 1)));
  const length = Math.max(22, step + 6);
  const beats = heroes.map((h, i) => {
    const start = FIRST_TICK + i * step;
    return beat(h, start, length, start + Math.max(step, 15) + 2);
  });
  return planOf(kind, startTick, Math.min(90, FIRST_TICK + (n - 1) * step + length + 3), beats);
}

/** Первый бой сессии: полная перекличка. */
export function sessionIntro(heroes: readonly HeroId[], startTick: number): IntroPlan {
  return rollCall("session", heroes, startTick);
}

/** Босс: та же полная перекличка, 3 с; пауза волны босса под неё длиннее (`waves.bossBreakTicks`). */
export function bossIntro(heroes: readonly HeroId[], startTick: number): IntroPlan {
  return rollCall("boss", heroes, startTick);
}

/** Длина плашки «Волна N» на x1, тиков: 1 с, внутри паузы волны в 2 с. */
export const WAVE_PLATE_TICKS = 30;

/**
 * Плашка «Волна N»: `length` тиков, позы и подписи уровня у героев, получивших уровень с прошлого боя, все сразу. Без
 * таких героев — одна плашка без поз.
 */
export function waveIntro(levelled: readonly HeroId[], startTick: number, length = WAVE_PLATE_TICKS): IntroPlan {
  return planOf(
    "wave",
    startTick,
    length,
    levelled.map((h) => beat(h, 0, Math.min(length, 26), length - 1)),
  );
}

/** Поза героя в тик волны `tick`; `null` — герой сейчас не показывается. */
export function introPose(plan: IntroPlan, heroId: HeroId, tick: number): IntroPose | null {
  const b = plan.beats.find((x) => x.heroId === heroId);
  if (!b) return null;
  const age = tick - plan.startTick - b.start;
  if (age < 0 || age > b.length) return null;
  const r = Math.min(1, age / HOP_TICKS);
  return { ticks: age, hop: Math.sin(r * Math.PI) * HOP_CELLS, glow: 0.25 * (1 - age / b.length) };
}

const fade = (age: number, life: number): number => Math.max(0, 1 - age / life);
const ease = (p: number): number => 1 - (1 - Math.min(1, Math.max(0, p))) ** 3;

/** Маленький эффект навыка на самом герое. `age` — тики от начала его позы, `length` — её длина. */
export function introFx(sink: IntroFxSink, heroId: HeroId, age: number, length: number): void {
  sink.ring(0, 0.05, 0.55 + 0.9 * ease(age / 15), 0.8 * fade(age, 15));
  if (heroId === "gunner") {
    for (let i = 0; i < 3; i++) {
      const a = age - 3 - i * 5;
      if (a < 0) continue;
      sink.sprite("muzzle", -0.26, -0.74, a, 1);
      const q = a / 7;
      if (q < 1)
        sink.tracer(-0.17 - (2.4 - i * 0.9) * q, -0.74 - (1.26 + i * 0.3) * q, -2.4 + i * 0.9, -1.26 - i * 0.3);
    }
  } else if (heroId === "bomber") {
    const q = (age - 3) / 13;
    if (q >= 0 && q < 1) sink.bomb(q * 0.65, -1 - Math.sin(q * Math.PI) + q * 0.56);
    if (age >= 16) {
      sink.sprite("explosion", 0.7, -0.3, age - 16, 1.25);
      sink.glow(0.7, -0.3, 2, 0.7 * fade(age - 16, 14));
    }
  } else if (heroId === "sparky") {
    sink.sprite("lightning", 0, 0, (age - 2) % 15, 1);
    if (age > 7) sink.sprite("lightning", 0.5, 0.1, age - 7, 0.8);
    if (age < 21) {
      sink.arc(-0.1, -1, -1, -0.17, 0.9);
      sink.arc(0.13, -1, 0.96, -0.3, 0.9);
    }
  } else if (heroId === "blaze") {
    for (let i = 0; i < 3; i++) sink.sprite("fire", -0.65 + i * 0.65, i === 1 ? -0.04 : 0.09, age - 2 - i * 2, 1.05);
    sink.glow(0, -0.26, 2.3, 0.55 * fade(age, Math.max(length, 27)));
  } else {
    sink.sprite("ice", 0, 0.13, age - 2, 1.2);
    sink.ring(0, 0.09, 0.45 + 0.8 * ease((age - 3) / 18), 0.8 * fade(age - 3, 21));
    sink.glow(0, -0.43, 2.1, 0.5 * fade(age, Math.max(length, 27)));
  }
}
